import * as THREE from "three";
import { Context } from "../core/context";
import { DbLine, FMathUtils } from "@/CadEngine";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "./IDrawOperation";

import { ULabel } from "@/ui/ULabel";
import {
  ActiveInputType,
  CircleArcInfo,
  DrawWallAcceptParams,
  drawWallType,
} from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";
import { IndserObject, IndsertPosition } from "@/core/IndserObject";
import { BPMathUtils } from "@/utils/BPMathUtils";
import { WasmUtil } from "@/utils/WasmUtil";

class DrawOperationWall implements IDrawOperation {
  private m_context: Context;
  private m_callback: (result: any) => void;
  private m_size = { x: 1, y: 1, z: 1 };
  private m_locationObject: THREE.Line;
  private m_auxLine: THREE.Line | undefined;
  private m_TextLabel: ULabel;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_points: THREE.Vector3[] = [];
  private m_tempObjectGroup: THREE.Group = new THREE.Group();
  private m_callbackUnderway: (point: THREE.Vector3) => void;
  private m_dimension: DimensionGroup;
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_acceptParams: DrawWallAcceptParams;
  private m_sumWidth: number = 0;
  private m_leftWallLineModel: THREE.Line;
  private m_rightWallLineModel: THREE.Line;
  private m_leftFaceLayerLineModel: THREE.Line;
  private m_rightFaceLayerLineModel: THREE.Line;
  private m_centerLineModel!: THREE.LineSegments;
  private m_currentCircleArcInfo: CircleArcInfo | undefined = undefined;
  private m_xAxis = new THREE.Vector3(1, 0, 0);
  private m_yAxis = new THREE.Vector3(0, 1, 0);
  private m_zAxis = new THREE.Vector3(0, 0, 1);
  private m_computerDatasPre: any[] = [];
  private m_rectanglePoints: THREE.Vector3[] = [];
  private m_textLabelLocation = new THREE.Vector3();
  private m_isError = false;
  private m_isEnter = false;
  private m_circleArcEndPoints: THREE.Vector3[] = [];
  private m_isShift: Boolean = false;

  constructor(context: Context, acceptParams: DrawWallAcceptParams) {
    this.m_context = context;
    this.m_callback = acceptParams.callBackFinish;
    this.m_callbackUnderway = acceptParams.callbackUnderway;
    this.m_acceptParams = acceptParams;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_TextLabel.Visibility = true;
    this.m_context.SceneManager.AuxModel.add(this.m_tempObjectGroup);
    this.m_dimension = new DimensionGroup(
      this.m_context.ViewManager,
      this.m_context.SceneManager.AuxModel
    );
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.m_sumWidth =
      acceptParams.leftWallWidth +
      acceptParams.rightWallWidth +
      acceptParams.leftFaceLayer +
      acceptParams.rightFaceLayer;
    this.m_leftWallLineModel = this.createTempWallLine(0x00ff00);
    this.m_rightWallLineModel = this.createTempWallLine(0xff00ff);
    this.m_leftFaceLayerLineModel = this.createTempWallLine(0xffffff);
    this.m_rightFaceLayerLineModel = this.createTempWallLine(0xffffff);
    this.m_context.Dom.style.cursor = "none";
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {
    if (!this.m_context.SelectionManager.getEnableSnap) {
      return;
    }
    if (!this.m_points.length) return;
    let tolSq = params.tol * params.tol;
    if (this.m_points.length > 0) {
      let start = new THREE.Vector3();
      let end = new THREE.Vector3();
      let target = new THREE.Vector3();
      let targetOnSegment = new THREE.Vector3();
      let point = this.m_points[this.m_points.length - 1];
      if (this.m_points.length > 1) {
        for (let index = 0; index < this.m_points.length - 1; index++) {
          start.copy(this.m_points[index]);
          end.copy(this.m_points[index + 1]);
          let distance = params.ray.distanceSqToSegment(
            start,
            end,
            target,
            targetOnSegment
          ); // target在射线上，targetOnSegment在线段上
          //  console.log(target.x,target.y,target.z+'          '+targetOnSegment.x,targetOnSegment.y,targetOnSegment.z)
          if (distance <= tolSq) {
            // let location = {x:Number(target.x.toFixed(3)), y:Number(target.y.toFixed(3)), z:Number(target.z.toFixed(3))};
            let location = {
              x: Number(targetOnSegment.x.toFixed(3)),
              y: Number(targetOnSegment.y.toFixed(3)),
              z: Number(targetOnSegment.z.toFixed(3)),
            };
            let center = new THREE.Vector3(
              (start.x + end.x) * 0.5,
              (start.y + end.y) * 0.5,
              (start.z + end.z) * 0.5
            );
            let d1 = start.distanceTo(targetOnSegment);
            let d2 = center.distanceTo(targetOnSegment);
            let d3 = end.distanceTo(targetOnSegment);
            let position = IndsertPosition.None;
            let min = Math.min(d1, d2, d3);
            let adjust = targetOnSegment.clone();
            if (min <= params.tol) {
              if (min == d1) {
                position = IndsertPosition.Start;
                adjust = start.clone();
              }
              if (min == d2) {
                position = IndsertPosition.Center;
                adjust = center;
              }
              if (min == d3) {
                position = IndsertPosition.End;
                adjust = end.clone();
              }
            }
            let insert = new IndserObject(location, distance, "", []);
            insert.adjust = adjust;
            insert.position = position;
            insert.geo = new THREE.Line3(start.clone(), end.clone());
            params.array.push(insert);
          }
        } // 以上先是用当前移动线检测是否和自己画的线段相交，不包括最后一个线段
        //夹角矫正
        {
          // 这段代码作用为用当前移动线段来和自己画的线的最后一段线检测是否相交
          // alert('yyyyyyyy')
          let direction = new THREE.Vector3();
          this.m_context.ViewManager.Camera.getWorldDirection(direction);
          let line = new THREE.Line3(
            this.m_points[this.m_points.length - 2].clone(),
            this.m_points[this.m_points.length - 1].clone()
          );
          let p0 = new THREE.Vector3();
          let p = params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
          let dir0 = line.end.clone().sub(line.start).normalize();
          let dir1 = p0.clone().sub(line.end).normalize();
          let rad = BPMathUtils.angleTo(dir0, dir1, direction);
          let angle = (rad * 180) / Math.PI;
          let orginLength = p0.distanceTo(line.end);
          let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2; // orginLength越小自动偏转幅度越大
          let offsetAngle = (angle + tolAngle) % 90;
          if (offsetAngle <= tolAngle * 2) {
            //五度之内视为垂直
            rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
            let p = dir0
              .applyAxisAngle(direction, rad)
              .multiplyScalar(p0.distanceTo(line.end));
            p = line.end.clone().add(dir0);
            let insert = new IndserObject(
              p,
              Math.sqrt(params.ray.distanceSqToPoint(p)),
              "",
              []
            );
            insert.adjust = p;
            insert.position = IndsertPosition.AuxOrthogonal;
            params.array.push(insert);
          }
        }
      }
      //正交矫正
      {
        // 这段代码作用为 当只有一个点的时候移动线段和 X轴的相交情况
        let direction = new THREE.Vector3();
        this.m_context.ViewManager.Camera.getWorldDirection(direction);
        let start = this.m_points[this.m_points.length - 1].clone();
        let p0 = new THREE.Vector3();
        params.ray.intersectPlane(new THREE.Plane(direction, 0), p0);
        let dir0 = new THREE.Vector3(1, 0, 0);
        let dir1 = p0.clone().sub(start).normalize();
        let rad = BPMathUtils.angleTo(dir0, dir1, direction);
        let angle = (rad * 180) / Math.PI;
        let orginLength = p0.distanceTo(start);
        let tolAngle = (((params.tol / orginLength) * 180) / Math.PI) * 2;
        let offsetAngle = (angle + tolAngle) % 90;
        if (offsetAngle <= tolAngle * 2) {
          //五度之内视为垂直
          rad = rad - ((offsetAngle - tolAngle) * Math.PI) / 180;
          let p = dir0
            .applyAxisAngle(direction, rad)
            .multiplyScalar(p0.distanceTo(start));
          p = start.clone().add(dir0);
          let insert = new IndserObject(
            p,
            Math.sqrt(params.ray.distanceSqToPoint(p)),
            "",
            []
          );
          insert.adjust = p;
          insert.position = IndsertPosition.AuxOrthogonal;
          // insert.geo = new THREE.Line3(start.clone(), end.clone());
          params.array.push(insert);
        }
      }
    }
    let ray = new THREE.Ray();
    params.array.forEach((item) => {
      if (item.position == "none") item.position = IndsertPosition.Insert;
      if (item.geo) {
        if (item.geo instanceof THREE.Line3) {
          let line = item.geo as THREE.Line3;
          let p0 = item.adjust ?? item.location;
          let v0 = line.start.clone().sub(line.end.clone());
          let dir0 = v0.clone().normalize();
          // 检测是否是圆心 start
          let dis = line.start.clone().distanceTo(line.end.clone());
          if (item.obejct?.length) {
            let insertObj = item.obejct[0];
            if (insertObj instanceof DbLine) {
              if (dis == 0 && insertObj.sphere?.radius == 1) {
                item.position = IndsertPosition.Start;
                item.adjust = insertObj.sphere?.center;
                return;
              }
            }
          }
          // 检测是否是圆心 end
          if (this.m_points.length > 0) {
            let point = this.m_points[this.m_points.length - 1];
            let v1 = new THREE.Vector3(
              p0.x - point.x,
              p0.y - point.y,
              p0.z - point.z
            );
            let offset = dir0.multiplyScalar(v1.dot(dir0));
            if (offset.lengthSq() <= params.tol * params.tol) {
              //正交矫正
              item.position = IndsertPosition.Vertical;
              item.adjust = new THREE.Vector3(
                p0.x - offset.x,
                p0.y - offset.y,
                p0.z - offset.z
              );
            }
          }
          ray.set(line.start, line.end.clone().sub(line.start).normalize()); // 这里开始检测移动线段是否和场景中两个或两个以上的交点处接触
          params.array.forEach((item1) => {
            if (item1.geo instanceof THREE.Line3) {
              let line1 = item1.geo as THREE.Line3;
              // console.log(params,line,line1,line.start.clone().distanceTo(line1.start.clone()),'!!!!!!!!!');
              if (
                line != line1 &&
                line.start.clone().distanceTo(line1.start.clone()) >
                  0.0000000001 &&
                line.end.clone().distanceTo(line1.end.clone()) >
                  0.00000000001 &&
                line.start.y != line1.end.y
              ) {
                let target = new THREE.Vector3();
                let target1 = new THREE.Vector3();
                let distance = ray.distanceSqToSegment(
                  line1.start.clone(),
                  line1.end.clone(),
                  target
                );
                if (distance < 0.00000000001) {
                  line.closestPointToPoint(target, true, target1); //
                  if (target.distanceTo(target1) < 0.00000001) {
                    // 说明这两条线交叉了，因为这个点同时在两根线上
                    item.position = IndsertPosition.Cross;
                    item.adjust = target;
                    item.distance = target.distanceTo(
                      new THREE.Vector3(
                        item.location.x,
                        item.location.y,
                        item.location.z
                      )
                    );
                  }
                }
              }
            }
          });
        }
      }
    });
  }
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      !this.m_isEnter &&
      this.m_points.length &&
      this.m_acceptParams.drawWallType != drawWallType.drawRectangle
    ) {
      this.m_curPoint = this.m_context.ToolsManager.GetOrthogonalityPoint(
        point.point,
        this.m_points[this.m_points.length - 1]
      );
    }

    this.updatedTextLabel();
    this.updatedAuxLine();
    this.m_activeInputType = ActiveInputType.None;
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_inputValue = "";
  }
  updatedAuxLine() {
    if (this.m_points.length > 0) {
      this.m_dimension.Line.setVisible(true);
      this.m_dimension.Arc.setVisible(true);
      this.updateTempWallLine(this.m_acceptParams.drawWallType);
    } else {
      this.m_auxLine?.geometry.setFromPoints([]);
    }
    this.updateDimension();
  }

  OnDownPoint(point: DrawOperationMovePointParams): void {
    this.m_isError = false;
    if (
      (this.m_context.DrawManager.isEnableQuadrature || this.m_isShift) &&
      point.position == "none"
    ) {
      this.m_points.push(this.m_curPoint);
    } else {
      this.m_points.push(point.point.clone());
    }
    if (this.m_points.length == 1) {
      this.m_auxLine = this.m_context.ToolsManager.CreatedAuxLine();
    }
    if (this.m_acceptParams.drawWallType == drawWallType.drawLine) {
      if (this.m_points.length > 1) {
        this.computerWallPoints();
      }
    } else if (this.m_acceptParams.drawWallType == drawWallType.drawCircleArc) {
      if (this.m_points.length == 2) {
        this.m_dirNormal = this.m_curPoint
          .clone()
          .sub(this.m_points[0])
          .normalize();
        this.m_centerLineModel = this.createCenterLine(0xff0000);
        this.updateTempWallLineUsedDrawLine(this.m_points[0]);
      } else if (this.m_points.length == 3) {
        this.resetTempWallLineModel();
        if (this.m_currentCircleArcInfo) {
          this.computerWallPoints(drawWallType.drawCircleArc);
          this.m_currentCircleArcInfo = undefined;
        } else {
          this.m_points.pop();
          this.computerWallPoints();
        }
        if (!this.m_isError) {
          this.m_circleArcEndPoints.push(this.m_points[1]);
          this.m_points = [this.m_points[1]];
        }
      }
    } else if (this.m_acceptParams.drawWallType == drawWallType.drawRectangle) {
      if (this.m_points.length == 1) {
        this.m_centerLineModel = this.createCenterLine(0xff0000);
      }
      if (this.m_points.length == 2) {
        this.computerWallPoints(drawWallType.drawRectangle);
        this.m_rectanglePoints.length = 0;
        this.resetTempWallLineModel();
        this.m_auxLine?.geometry.setFromPoints([]);
        this.m_points.length = 0;
        this.updateDimension();
      }
    }
    this.m_callbackUnderway(point.point.clone());
  }
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.keyCode == 16) {
      this.m_isShift = true;
    }
    if (e.key == "Escape") {
      this.m_context.DrawManager.DrawFinish(DrawOperateState.complete);
    }
    if (
      !this.m_points.length &&
      this.m_acceptParams.drawWallType != drawWallType.drawRectangle
    ) {
      return;
    }
    this.m_angleInputValue = this.m_dimension.Arc.Input.Dom.value;
    this.m_distanceInputValue = this.m_dimension.Line.Input.Dom.value;
    if (e.ctrlKey && (e.key == "z"||e.key=="Z")) {
      this.m_isError = false;
      if (this.m_acceptParams.drawWallType == drawWallType.drawLine) {
        this.m_points?.pop();
        this.m_computerDatasPre?.pop();
        this.m_computerDatasPre?.pop();
      } else if (
        this.m_acceptParams.drawWallType == drawWallType.drawRectangle
      ) {
        if (!this.m_points.length) {
          this.m_computerDatasPre = this.m_computerDatasPre.slice(
            0,
            this.m_computerDatasPre.length - 4
          );
        } else {
          this.m_rectanglePoints.length = 0;
          this.m_points.length = 0;
        }
      } else if (
        this.m_acceptParams.drawWallType == drawWallType.drawCircleArc
      ) {
        this.m_computerDatasPre?.pop();
        this.m_circleArcEndPoints.pop();
        this.m_currentCircleArcInfo = undefined;
        if (this.m_circleArcEndPoints[this.m_circleArcEndPoints.length - 1]) {
          this.m_points = [
            this.m_circleArcEndPoints[this.m_circleArcEndPoints.length - 1],
          ];
        } else {
          this.m_points = [];
        }
      }
      this.updatedTextLabel();
      this.updatedAuxLine();
      this.computerWallPoints(this.m_acceptParams.drawWallType);
      if (
        !this.m_points.length ||
        this.m_acceptParams.drawWallType == drawWallType.drawCircleArc
      ) {
        this.resetTempWallLineModel();
      }
      this.m_context.ViewManager.Render();
      // this.m_callbackUnderway(this.m_points[this.m_points.length-1].clone());
    }

    if (e.key === "Tab") {
      this.m_inputValue = "";
      if (this.m_activeInputType == ActiveInputType.None) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      } else if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_activeInputType = ActiveInputType.Angle;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_activeInputType = ActiveInputType.Distance;
        this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
        this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      }
    }
    if (e.key === "Backspace") {
      if (this.m_inputValue.length > 0) {
        this.m_inputValue = this.m_inputValue.substring(
          0,
          this.m_inputValue.length - 1
        );
      }
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_distanceInputValue = this.m_inputValue;
        }
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue);
        if (this.m_inputValue) {
          this.m_angleInputValue = this.m_inputValue;
        }
      }
    } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
      if (this.m_activeInputType == ActiveInputType.None) return;
      if (this.m_inputValue?.includes(".") && e.key == ".") return;
      this.m_inputValue += e.key;
      if (this.m_activeInputType == ActiveInputType.Distance) {
        this.m_dimension.Line.Input.SetVaule(this.m_inputValue);
        this.m_distanceInputValue = this.m_inputValue;
      } else if (this.m_activeInputType == ActiveInputType.Angle) {
        this.m_dimension.Arc.Input.SetVaule(this.m_inputValue);
        this.m_angleInputValue = this.m_inputValue;
      }
    } else if (e.key == "Enter") {
      let length = parseFloat(this.m_distanceInputValue) * 0.001;
      let angle = THREE.MathUtils.degToRad(parseFloat(this.m_angleInputValue));
      let dir = new THREE.Vector3(1, 0, 0);
      dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
      dir.multiplyScalar(length);
      this.m_curPoint = this.m_points[this.m_points.length - 1]
        .clone()
        .add(dir);
      if (this.m_acceptParams.drawWallType == drawWallType.drawRectangle) {
        this.updateTempWallLineUsedDrawRectangle(
          this.m_points[this.m_points.length - 1],
          this.m_curPoint
        );
      } else if (
        this.m_acceptParams.drawWallType == drawWallType.drawCircleArc
      ) {
        if (this.m_points.length > 1) {
          this.updateTempWallLine(drawWallType.drawCircleArc);
        }
      }
      this.m_isEnter = true;
      this.OnDownPoint({
        point: this.m_curPoint,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.OnMovePoint({
        point: this.m_curPoint,
        geo: undefined,
        position: IndsertPosition.None,
      });
      this.m_isEnter = false;
      this.m_context.ViewManager.Render();
    }
  }
  onKeyUp(e: KeyboardEvent) {
    if (e.keyCode == 16) {
      this.m_isShift = false;
    }
  }
  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: DrawOperateState) {
    this.clear();
    if (operate == DrawOperateState.complete) {
      this.m_callback(this.m_computerDatasPre);
    } else if (operate == DrawOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {}

  onMouseUp(e: PointerEvent): void {}

  private updatedTextLabel() {
    this.m_textLabelLocation.x = this.m_curPoint.x + this.Scale * 65;
    this.m_textLabelLocation.y = this.m_curPoint.y - this.Scale * 10;

    if (this.m_isError) {
      if (this.m_acceptParams.drawWallType != drawWallType.drawRectangle) {
        this.m_textLabelLocation.x = this.m_curPoint.x + this.Scale * 85;
        this.m_TextLabel.SetVaule(
          "绘制错误,请重试！按Ctrl+Z键将撤销回退到上一点,长按Shift锁定正交",
          this.m_textLabelLocation,
          new THREE.Vector3(1, 0, 0)
        );
      } else {
        this.m_TextLabel.SetVaule(
          "绘制错误,请重试！按Ctrl+Z键将撤销回退到上一点",
          this.m_textLabelLocation,
          new THREE.Vector3(1, 0, 0)
        );
      }
    } else {
      if (this.m_acceptParams.drawWallType != drawWallType.drawRectangle) {
        this.m_TextLabel.SetVaule(
          "按Ctrl+Z键将撤销回退到上一点,长按Shift锁定正交",
          this.m_textLabelLocation,
          new THREE.Vector3(1, 0, 0)
        );
      } else {
        this.m_textLabelLocation.x = this.m_curPoint.x + this.Scale * 45;
        this.m_TextLabel.SetVaule(
          "按Ctrl+Z键将撤销回退到上一点",
          this.m_textLabelLocation,
          new THREE.Vector3(1, 0, 0)
        );
      }
    }
  }

  private createCenterLine(color: number) {
    let geo = new THREE.BufferGeometry();
    let material = new THREE.LineBasicMaterial({ color });
    let line = new THREE.LineSegments(geo, material);
    line.frustumCulled = false;
    this.m_tempObjectGroup.add(line);
    return line;
  }

  private updateDimension() {
    if (this.m_points.length) {
      this.m_dimension.Line.SetDimension(
        this.m_points[this.m_points.length - 1],
        this.m_curPoint,
        this.Scale
      );
      this.m_dimension.Arc.SetDimension(
        this.m_points[this.m_points.length - 1],
        new THREE.Vector3(1, 0, 0),
        this.CurDir,
        this.Scale
      );
    } else {
      this.m_dimension.Line.setVisible(false);
      this.m_dimension.Arc.setVisible(false);
    }
  }

  private computerWallPoints(type: drawWallType = drawWallType.drawLine) {
    // if (this.m_points.length < 1) return;
    let { leftWallWidth, rightWallWidth, leftFaceLayer, rightFaceLayer } =
      this.m_acceptParams;
    let resultData: any = [];
    let computerDatas: any[] = [...this.m_computerDatasPre];
    if (type == drawWallType.drawLine) {
      if (this.m_points.length < 2) {
        resultData = [];
      } else {
        computerDatas.push({
          type,
          dataInfo: {
            startPoint: this.m_points[this.m_points.length - 2],
            endPoint: this.m_points[this.m_points.length - 1],
          },
        });
        resultData = WasmUtil.Draw_Wall_Road(
          this.m_context,
          {
            leftWallWidth,
            rightWallWidth,
            leftFaceLayer,
            rightFaceLayer,
          },
          computerDatas
        );
      }
    } else if (type == drawWallType.drawCircleArc) {
      if (this.m_currentCircleArcInfo) {
        computerDatas.push({
          type,
          dataInfo: this.m_currentCircleArcInfo,
        });
      }
      resultData = WasmUtil.Draw_Wall_Road(
        this.m_context,
        {
          leftWallWidth,
          rightWallWidth,
          leftFaceLayer,
          rightFaceLayer,
        },
        computerDatas
      );
    } else if (type == drawWallType.drawRectangle) {
      if (this.m_rectanglePoints.length) {
        for (let i = 0; i < this.m_rectanglePoints.length - 1; i++) {
          computerDatas.push({
            type: drawWallType.drawLine,
            dataInfo: {
              startPoint: this.m_rectanglePoints[i],
              endPoint: this.m_rectanglePoints[i + 1],
            },
          });
        }
      }
      if (!computerDatas.length) {
        resultData = [];
      } else {
        resultData = WasmUtil.Draw_Wall_Road(
          this.m_context,
          {
            leftWallWidth,
            rightWallWidth,
            leftFaceLayer,
            rightFaceLayer,
          },
          computerDatas
        );
      }
    }
    if (!resultData) {
      this.m_isError = true;
      if (type == drawWallType.drawRectangle) {
        this.m_rectanglePoints.length = 0;
      } else if (type == drawWallType.drawLine) {
        this.m_points.pop();
        this.m_computerDatasPre.pop();
      } else if (type == drawWallType.drawCircleArc) {
        this.m_currentCircleArcInfo = undefined;
        this.m_points = [this.m_points[0]];
        console.log("error");
      }
      this.computerWallPoints(type);
    } else {
      this.m_computerDatasPre = [...computerDatas];
      this.updatedWall(resultData);
    }
  }
  private updatedWall(resultData: any[]) {
    for (let i = 0; i < this.m_tempObjectGroup.children.length; i++) {
      const element = this.m_tempObjectGroup.children[i];
      if (element.name === "wallLine" && element instanceof THREE.Line) {
        element.geometry.dispose();
        element.material.dispose();
        element.removeFromParent();
        i--;
      }
    }
    this.m_context.ToolsManager.ObjectsClear([this.m_centerLineModel]);
    for (let i = 0; i < resultData.length; i++) {
      let geometry = new THREE.BufferGeometry();
      geometry.setFromPoints(resultData[i]);
      let material = new THREE.LineBasicMaterial();
      let line = new THREE.Line(geometry, material);
      line.name = "wallLine";
      this.m_tempObjectGroup.add(line);
    }
  }
  private createTempWallLine(color: number) {
    let geometry = new THREE.BufferGeometry();
    let material = new THREE.LineBasicMaterial({ color: color });
    let line = new THREE.Line(geometry, material);
    line.frustumCulled = false;
    this.m_tempObjectGroup.add(line);
    return line;
  }

  private updateTempWallLine(drawType: drawWallType) {
    let points: THREE.Vector3[] = [];
    if (drawType == drawWallType.drawLine) {
      this.m_dirNormal = this.CurDir.clone().normalize();
      points.push(this.m_points[this.m_points.length - 1], this.m_curPoint);
      this.updateTempWallLineUsedDrawLine();
    } else if (drawType === drawWallType.drawCircleArc) {
      points = [this.m_points[this.m_points.length - 1], this.m_curPoint];
      if (this.m_points.length > 1) {
        this.updateTempWallLineUsedDrawCircleArc();
      }
    } else if (drawType === drawWallType.drawRectangle) {
      points.push(...this.m_points, this.m_curPoint);
      this.updateTempWallLineUsedDrawRectangle(
        this.m_points[0],
        this.m_curPoint
      );
    }
    this.m_auxLine?.geometry.setFromPoints(points);
    this.m_auxLine?.computeLineDistances();
  }
  private updateTempWallLineUsedDrawLine(
    startP: THREE.Vector3 = this.m_points[this.m_points.length - 1],
    endP: THREE.Vector3 = this.m_curPoint
  ) {
    let leftWallStartP = this.getTempWallLinePointsUsedDrawLine({
      sourceP: startP,
      length: this.m_acceptParams.leftWallWidth,
      angle: Math.PI / 2,
      isNegate: false,
    });
    let leftWallEndP = this.getTempWallLinePointsUsedDrawLine({
      sourceP: endP,
      length: this.m_acceptParams.leftWallWidth,
      angle: -Math.PI / 2,
      isNegate: true,
    });
    this.m_leftWallLineModel?.geometry.setFromPoints([
      leftWallStartP,
      leftWallEndP,
    ]);

    let rightWallStartP = this.getTempWallLinePointsUsedDrawLine({
      sourceP: startP,
      length: this.m_acceptParams.rightWallWidth,
      angle: -Math.PI / 2,
      isNegate: false,
    });
    let rightWallEndP = this.getTempWallLinePointsUsedDrawLine({
      sourceP: endP,
      length: this.m_acceptParams.rightWallWidth,
      angle: Math.PI / 2,
      isNegate: true,
    });
    this.m_rightWallLineModel?.geometry.setFromPoints([
      rightWallStartP,
      rightWallEndP,
    ]);

    if (this.m_acceptParams.leftFaceLayer > 0) {
      let leftFaceLayerStartP = this.getTempWallLinePointsUsedDrawLine({
        sourceP: startP,
        length:
          this.m_acceptParams.leftWallWidth + this.m_acceptParams.leftFaceLayer,
        angle: Math.PI / 2,
        isNegate: false,
      });
      let leftFaceLayerEndP = this.getTempWallLinePointsUsedDrawLine({
        sourceP: endP,
        length:
          this.m_acceptParams.leftWallWidth + this.m_acceptParams.leftFaceLayer,
        angle: -Math.PI / 2,
        isNegate: true,
      });
      this.m_leftFaceLayerLineModel?.geometry.setFromPoints([
        leftFaceLayerStartP,
        leftFaceLayerEndP,
      ]);
    }
    if (this.m_acceptParams.rightFaceLayer > 0) {
      let rightFaceLayerStartP = this.getTempWallLinePointsUsedDrawLine({
        sourceP: startP,
        length:
          this.m_acceptParams.rightWallWidth +
          this.m_acceptParams.rightFaceLayer,
        angle: -Math.PI / 2,
        isNegate: false,
      });
      let rightFaceLayerEndP = this.getTempWallLinePointsUsedDrawLine({
        sourceP: endP,
        length:
          this.m_acceptParams.rightWallWidth +
          this.m_acceptParams.rightFaceLayer,
        angle: Math.PI / 2,
        isNegate: true,
      });
      this.m_rightFaceLayerLineModel?.geometry.setFromPoints([
        rightFaceLayerStartP,
        rightFaceLayerEndP,
      ]);
    }
  }
  private getTempWallLinePointsUsedDrawLine(params: {
    sourceP: THREE.Vector3;
    length: number;
    angle: number;
    isNegate: boolean;
  }) {
    let dir = this.m_dirNormal.clone();
    if (params.isNegate) {
      dir.negate();
    }
    return dir
      .clone()
      .setLength(params.length)
      .applyAxisAngle(this.m_zAxis, params.angle)
      .add(params.sourceP);
  }

  private computerCircleArc() {
    let vect1 = this.m_points[1].clone().sub(this.m_points[0]).normalize();
    let center1StartP = new THREE.Vector3().lerpVectors(
      this.m_points[0],
      this.m_points[1],
      0.5
    );
    let center1EndP = center1StartP
      .clone()
      .add(vect1.clone().applyAxisAngle(this.m_zAxis, Math.PI / 2));
    let line1: any = {
      points: [
        center1StartP.x,
        center1StartP.y,
        center1StartP.z,
        center1EndP.x,
        center1EndP.y,
        center1EndP.z,
      ],
    };

    let vect2 = this.m_curPoint.clone().sub(this.m_points[1]).normalize();

    let center2StartP = new THREE.Vector3().lerpVectors(
      this.m_points[1],
      this.m_curPoint,
      0.5
    );
    let center2EndP = center2StartP
      .clone()
      .add(vect2.clone().applyAxisAngle(this.m_zAxis, Math.PI / 2));
    let line2: any = {
      points: [
        center2StartP.x,
        center2StartP.y,
        center2StartP.z,
        center2EndP.x,
        center2EndP.y,
        center2EndP.z,
      ],
    };
    let center = FMathUtils.GetLineIntersection(
      line1 as DbLine,
      line2 as DbLine
    );
    if (!center) return undefined;
    let radius = this.m_points[0].distanceTo(center);
    let radiusVector1 = new THREE.Vector3()
      .subVectors(this.m_points[0], center)
      .normalize();
    let radiusVector2 = new THREE.Vector3()
      .subVectors(this.m_points[1], center)
      .normalize();
    let startAngle = radiusVector1.clone().angleTo(this.m_xAxis);
    let endAngle = radiusVector2.clone().angleTo(this.m_xAxis);
    let clockwise = true;
    let crossResult = Math.abs(vect2.clone().cross(vect1).z);
    if (crossResult < 0.001) {
      return undefined;
    } else {
      if (vect2.clone().cross(vect1).z > 0) {
        clockwise = false;
      }
      if (radiusVector1.clone().cross(this.m_xAxis).z > 0) {
        startAngle = Math.PI * 2 - startAngle;
      }
      if (radiusVector2.clone().cross(this.m_xAxis).z > 0) {
        endAngle = Math.PI * 2 - endAngle;
      }
      return {
        center,
        radius,
        startAngle,
        endAngle,
        clockwise,
        startPoint: this.m_points[0],
        endPoint: this.m_points[1],
      };
    }
  }
  private getTempWallLinePointsUsedDrawCircleArc(params: CircleArcInfo) {
    let { center, radius, startAngle, endAngle, clockwise } = params;
    let curve = new THREE.EllipseCurve(
      center.x,
      center.y,
      radius,
      radius,
      startAngle,
      endAngle,
      clockwise,
      0
    );
    return curve
      .getPoints(50)
      .map((point) => new THREE.Vector3(point.x, point.y, 0));
  }
  private updateTempWallLineUsedDrawCircleArc() {
    let circleArcInfo = this.computerCircleArc();
    this.m_currentCircleArcInfo = circleArcInfo;
    if (circleArcInfo) {
      let { center, radius, startAngle, endAngle, clockwise } = circleArcInfo;
      let circleArcCenterLinePoints =
        this.getTempWallLinePointsUsedDrawCircleArc({
          center,
          radius,
          startAngle,
          endAngle,
          clockwise,
        });
      let leftWallLineModelPoints = this.getTempWallLinePointsUsedDrawCircleArc(
        {
          center,
          radius: radius + this.m_acceptParams.leftWallWidth,
          startAngle,
          endAngle,
          clockwise,
        }
      );
      let rightWallLineModelPoints =
        this.getTempWallLinePointsUsedDrawCircleArc({
          center,
          radius: radius - this.m_acceptParams.rightWallWidth,
          startAngle,
          endAngle,
          clockwise,
        });
      if (this.m_acceptParams.leftFaceLayer > 0) {
        let leftFaceLayerLineModelPoints =
          this.getTempWallLinePointsUsedDrawCircleArc({
            center,
            radius:
              radius +
              this.m_acceptParams.leftWallWidth +
              this.m_acceptParams.leftFaceLayer,
            startAngle,
            endAngle,
            clockwise,
          });
        this.m_leftFaceLayerLineModel?.geometry.setFromPoints(
          leftFaceLayerLineModelPoints
        );
      }
      if (this.m_acceptParams.rightFaceLayer > 0) {
        let rightFaceLayerLineModelPoints =
          this.getTempWallLinePointsUsedDrawCircleArc({
            center,
            radius:
              radius -
              this.m_acceptParams.rightWallWidth -
              this.m_acceptParams.rightFaceLayer,
            startAngle,
            endAngle,
            clockwise,
          });
        this.m_rightFaceLayerLineModel?.geometry.setFromPoints(
          rightFaceLayerLineModelPoints
        );
      }

      this.m_centerLineModel.geometry.setFromPoints(circleArcCenterLinePoints);
      this.m_leftWallLineModel?.geometry.setFromPoints(leftWallLineModelPoints);
      this.m_rightWallLineModel?.geometry.setFromPoints(
        rightWallLineModelPoints
      );
    } else {
      let lineCurve = new THREE.LineCurve3(this.m_points[0], this.m_points[1]);
      let points = lineCurve.getSpacedPoints(50);
      this.updateTempWallLineUsedDrawLine(this.m_points[0], this.m_points[1]);
      this.m_centerLineModel.geometry.setFromPoints(points);
    }
  }

  private updateTempWallLineUsedDrawRectangle(
    start: THREE.Vector3,
    end: THREE.Vector3
  ) {
    let p1 = new THREE.Vector3(end.x, start.y, start.z);
    let p2 = new THREE.Vector3(start.x, end.y, start.z);
    let points: THREE.Vector3[] = [start, p1, end, p2, start];
    this.m_rectanglePoints = [...points];
    let rectangleCenterLinePoints =
      this.getTempWallLinePointsUsedDrawRectangle(points);
    let rectangleLeftWallLinePoints =
      this.getTempWallLinePointsUsedDrawRectangle(
        points,
        this.m_acceptParams.leftWallWidth,
        "left"
      );
    let rectangleLeftFaceLayerLinePoints =
      this.getTempWallLinePointsUsedDrawRectangle(
        points,
        this.m_acceptParams.leftWallWidth + this.m_acceptParams.leftFaceLayer,
        "left"
      );
    let rectangleRightWallLinePoints =
      this.getTempWallLinePointsUsedDrawRectangle(
        points,
        this.m_acceptParams.rightWallWidth,
        "right"
      );
    let rectangleRightFaceLayerLinePoints =
      this.getTempWallLinePointsUsedDrawRectangle(
        points,
        this.m_acceptParams.rightWallWidth + this.m_acceptParams.rightFaceLayer,
        "right"
      );
    if (
      Math.abs(this.CurDir.clone().cross(this.m_xAxis).z) > 0.00001 &&
      Math.abs(this.CurDir.clone().cross(this.m_yAxis).z) > 0.00001
    ) {
      this.m_centerLineModel.geometry.setFromPoints(rectangleCenterLinePoints);
      this.m_leftWallLineModel.geometry.setFromPoints(
        rectangleLeftWallLinePoints
      );
      this.m_leftFaceLayerLineModel.geometry.setFromPoints(
        rectangleLeftFaceLayerLinePoints
      );
      this.m_rightWallLineModel.geometry.setFromPoints(
        rectangleRightWallLinePoints
      );
      this.m_rightFaceLayerLineModel.geometry.setFromPoints(
        rectangleRightFaceLayerLinePoints
      );
    }
    // else{
    //   this.m_centerLineModel.geometry.setFromPoints(rectangleCenterLinePoints);
    // }
  }

  private getTempWallLinePointsUsedDrawRectangle(
    points: THREE.Vector3[] = [],
    length: number = 0,
    dir: "left" | "right" | "center" = "center"
  ) {
    let angle = Math.PI / 2;
    let crossAngle = this.CurDir.angleTo(this.m_xAxis);
    if (this.CurDir.clone().cross(this.m_xAxis).z > 0) {
      crossAngle = Math.PI * 2 - crossAngle;
    }
    if (
      (crossAngle >= 0 && crossAngle <= Math.PI / 2) ||
      (crossAngle <= (Math.PI * 3) / 2 && crossAngle >= Math.PI)
    ) {
      angle = -angle;
    }

    // Math.abs(this.CurDir.clone().cross(yAxis).z) < 0.0001
    let resultPoints: THREE.Vector3[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      let p1 = points[i].clone();
      let p2 = points[i + 1].clone();

      let startP = p1.clone();
      let endP = p2.clone();
      if (dir == "center") {
        let lineCurve = new THREE.LineCurve3(startP, endP);
        let points = lineCurve.getSpacedPoints(50);
        resultPoints.push(...points);
      }

      if (length > 0) {
        let v1 = p2.clone().sub(p1).normalize().setLength(length);
        let v2 = p1.clone().sub(p2).normalize().setLength(length);

        if (dir == "left") {
          p1.add(v2);
          p2.add(v1);
          v1.applyAxisAngle(this.m_zAxis, angle);
          v2.applyAxisAngle(this.m_zAxis, -angle);
        } else if (dir == "right") {
          p1.add(v2.clone().negate());
          p2.add(v1.clone().negate());
          v1.applyAxisAngle(this.m_zAxis, -angle);
          v2.applyAxisAngle(this.m_zAxis, angle);
        }
        startP = p1.clone().add(v1);
        endP = p2.clone().add(v2);
        if (i == 0) {
          resultPoints.push(startP);
        }
        resultPoints.push(endP);
      }
    }

    return resultPoints;
  }

  private resetTempWallLineModel() {
    this.m_centerLineModel?.geometry.setFromPoints([]);
    this.m_leftFaceLayerLineModel?.geometry.setFromPoints([]);
    this.m_rightFaceLayerLineModel?.geometry.setFromPoints([]);
    this.m_leftWallLineModel?.geometry.setFromPoints([]);
    this.m_rightWallLineModel?.geometry.setFromPoints([]);
  }

  private clear() {
    this.m_dimension?.dispose();
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLine!,
    ]);
    this.m_context.ToolsManager.GroupClear(this.m_tempObjectGroup);
    this.m_TextLabel?.dispose();
  }
  private m_dirNormal: THREE.Vector3 = new THREE.Vector3();
  private get CurDir() {
    return this.m_curPoint.clone().sub(this.m_points[this.m_points.length - 1]);
  }
  private get Scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
}

export { DrawOperationWall };
